import { FiGrid, FiUser, FiShoppingBag, FiCreditCard, FiMapPin, FiBox, FiAward, FiPercent, FiImage, FiHome, FiShield } from "react-icons/fi";
import type { IconType } from "react-icons";

export interface ManualStep {
  title: string;
  description: string;
  image?: string;
}

export interface ManualTip {
  type: "tip" | "warning" | "info";
  text: string;
}

export interface ManualSection {
  id: string;
  title: string;
  icon: IconType;
  shortDescription: string;
  path?: string; // link to the actual page in the portal
  overview: string;
  steps: ManualStep[];
  tips: ManualTip[];
  features: string[];
}

export const manualSections: ManualSection[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: FiGrid,
    shortDescription: "Your command center for revenue, rankings, and key performance metrics.",
    path: "/dashboard",
    overview:
      "The Dashboard serves as the central intelligence hub for your dealership. Designed for quick consumption, it provides a real-time, comprehensive snapshot of your business health. From tracking your progress toward the next pricing tier to monitoring your national ranking and reviewing financial statistics, the Dashboard aggregates your most critical data into one actionable view. It is your daily briefing to ensure you are maximizing your partnership with DECO TV Frames.",
    steps: [
      {
        title: "1. The Welcome & Announcements Carousel",
        description:
          "At the very top of your dashboard, you'll find the personalized welcome carousel. This section highlights important portal announcements, new feature releases, and quick links to essential tools like the Dealer Manual and Locator Settings.",
        image: "https://via.placeholder.com/800x400/f3f4f6/1f2937?text=Welcome+Carousel+Preview",
      },
      {
        title: "2. Analyzing Your Revenue Progress",
        description:
          "The Revenue Progress module is the most critical metric on your dashboard. It displays your 'Lifetime Revenue' alongside your '365-Day Rolling Revenue'. Your rolling revenue directly dictates your current Tier Status (Bronze, Silver, Gold, or Platinum). The dynamic progress bar visually indicates exactly how much more revenue is required to unlock the wholesale discounts of the next tier.",
      },
      {
        title: "3. Understanding Your Dealer Ranking",
        description:
          "Just below your revenue stats, the Dealer Ranking module evaluates your performance against all active DECO TV Frames dealers nationwide. Your percentile rank and exact numerical position are calculated automatically based on your sales volume. Maintaining a high ranking directly influences your visibility and lead generation potential on the public Store Locator.",
      },
      {
        title: "4. Connecting with Your Sales Team",
        description:
          "The Contact Directory provides immediate access to your dedicated support team. You will find personalized cards for your assigned Sales Representative and the National Sales Manager. Use the 'Email' button for standard inquiries, or use the 'Book Meeting' feature to schedule a one-on-one consultation directly through their calendar.",
      },
      {
        title: "5. Financial & Order Statistics Snapshot",
        description:
          "The Payment Stats section aggregates your historical purchasing data directly from Shopify. It displays your total orders, recent transaction history, average order value, and your available Store Credit balance. Store credits displayed here can be applied immediately toward future wholesale purchases.",
      },
    ],
    tips: [
      { type: "info", text: "Your Tier Level upgrades automatically the moment your 365-day rolling revenue crosses a threshold. No manual review is required." },
      { type: "warning", text: "Because revenue data syncs directly from Shopify, please allow up to 24 hours for recent large orders to reflect in your progress bar and ranking." },
      { type: "tip", text: "Keep an eye on the gap to your next tier. Sometimes placing one additional stock order is enough to bump you into a higher discount bracket for the rest of the year." },
    ],
    features: [
      "Real-time 365-day rolling revenue tracking and tier progression visualization",
      "Dynamic national dealer ranking and percentile placement algorithms",
      "Direct calendar integration for booking meetings with your Sales Representative",
      "Live Shopify data sync for order history and financial statistics",
      "Instant visibility into available promotional store credit balances",
    ],
  },
  {
    id: "profile",
    title: "Dealer Profile",
    icon: FiUser,
    shortDescription: "Manage your business details, branding, and public listing info.",
    path: "/profile",
    overview:
      "Your Dealer Profile is the foundation of your presence on the DECO TV Frames platform. This is where you manage your company information, contact details, business hours, logo, and more. A complete profile not only builds trust with potential customers but also improves your visibility on the Store Locator.",
    steps: [
      {
        title: "Complete Your Business Information",
        description:
          "Fill in your company name, phone number, email, website URL, and physical address. Fields marked 'Yet to fill' are incomplete — filling them in improves your listing quality score.",
      },
      {
        title: "Upload Your Logo & Cover Image",
        description:
          "Click the edit button on your profile header to upload a high-resolution logo and a cover image. These appear on your Store Locator card and detail page. Use the built-in image cropper for perfect sizing.",
      },
      {
        title: "Set Your Business Hours",
        description:
          "Configure your weekly business hours so customers know when you're available. You can set different hours for each day or mark days as closed.",
      },
      {
        title: "Add a Business Description",
        description:
          "Write a compelling description of your dealership. This appears on your public listing and helps customers understand what sets you apart.",
      },
    ],
    tips: [
      { type: "tip", text: "Profiles with logos and complete information rank higher on the Store Locator. Complete every field for maximum visibility." },
      { type: "info", text: "Your profile changes are saved immediately and reflected on the Store Locator within minutes." },
      { type: "warning", text: "Make sure your address is accurate — it determines your pin placement on the locator map." },
    ],
    features: [
      "Inline editing with instant save for all fields",
      "'Yet to fill' indicators highlight missing information",
      "Built-in image cropper for logo and cover photo uploads",
      "Business hours configuration with day-by-day control",
      "Live preview of how your listing appears to customers",
    ],
  },
  {
    id: "addresses",
    title: "Saved Addresses",
    icon: FiHome,
    shortDescription: "Manage primary shipping and billing addresses for wholesale orders.",
    path: "/addresses",
    overview:
      "The Addresses page enables you to manage your default shipping and billing locations. Keeping your saved addresses updated ensures accurate tax calculation, fast checkout, and reliable order delivery from Shopify.",
    steps: [
      {
        title: "1. View Saved Addresses",
        description:
          "Review your saved primary shipping address and additional delivery locations on file.",
      },
      {
        title: "2. Update Address Details",
        description:
          "Edit street addresses, suite/unit numbers, city, state, and ZIP codes to ensure delivery carriers have exact details.",
      },
      {
        title: "3. Set Primary Default Address",
        description:
          "Mark your main warehouse or showroom address as default for automatic pre-fill during order placement.",
      },
    ],
    tips: [
      { type: "warning", text: "Ensure your ZIP code and address match official postal records to prevent freight shipping delays." },
      { type: "info", text: "Address changes automatically sync with your account for future purchases." },
    ],
    features: [
      "Primary shipping and billing address management",
      "Instant address editing and verification",
      "Default address selection for fast checkout",
    ],
  },
  {
    id: "orders",
    title: "Orders",
    icon: FiShoppingBag,
    shortDescription: "View your complete order history and shipment tracking.",
    path: "/orders",
    overview:
      "The Orders page displays your full purchase history with DECO TV Frames, pulled directly from Shopify. Track order statuses, view line items, and monitor shipments — all from one place.",
    steps: [
      {
        title: "Browse Your Order History",
        description:
          "All orders are listed in reverse chronological order (newest first). Each card shows the order number, date, total amount, and fulfillment status.",
      },
      {
        title: "View Order Details",
        description:
          "Click on any order to expand its details, including individual line items, quantities, pricing, and tracking information for shipped items.",
      },
      {
        title: "Track Shipments",
        description:
          "For fulfilled orders, tracking numbers and carrier links are provided so you can monitor delivery progress in real time.",
      },
    ],
    tips: [
      { type: "info", text: "Orders sync automatically from Shopify. If a recent order isn't showing, allow up to 15 minutes for it to appear." },
      { type: "tip", text: "Use the search functionality to quickly find a specific order by number or product name." },
    ],
    features: [
      "Complete Shopify order history with live sync",
      "Order status badges (Pending, Fulfilled, Partially Fulfilled)",
      "Line item breakdown with product images",
      "Shipment tracking links with carrier information",
    ],
  },
  {
    id: "payment-methods",
    title: "Payment Methods",
    icon: FiCreditCard,
    shortDescription: "Manage your saved payment methods and store credit.",
    path: "/saved-payment-methods",
    overview:
      "The Payment Methods page lets you view and manage your saved payment instruments. You can also see your current store credit balance, which can be applied toward future purchases.",
    steps: [
      {
        title: "View Saved Cards",
        description:
          "Your saved credit/debit cards are displayed with masked card numbers and expiration dates. These are securely stored through Shopify's payment system.",
      },
      {
        title: "Check Store Credit Balance",
        description:
          "Your available store credit is displayed prominently. Store credits are automatically applied during checkout on framemytv.com.",
      },
    ],
    tips: [
      { type: "info", text: "Store credits are issued by your sales representative or through promotional programs. They never expire." },
      { type: "tip", text: "Store credit balance is also visible in the sidebar under 'My Account' for quick reference." },
    ],
    features: [
      "Secure payment method management through Shopify",
      "Real-time store credit balance display",
      "Card type identification (Visa, Mastercard, etc.)",
    ],
  },
  {
    id: "store-listing",
    title: "Store Listing (Locator)",
    icon: FiMapPin,
    shortDescription: "Optimize your presence on the public dealer locator map.",
    path: "/coming-soon-locator",
    overview:
      "The Store Listing section is your gateway to the public-facing Dealer Locator. When fully launched, you'll be able to customize your locator profile, manage incoming leads, upload gallery images, list your services, and connect social media — all designed to drive customer engagement to your dealership.",
    steps: [
      {
        title: "Locator Settings",
        description:
          "Configure how your dealership appears on the Store Locator map, including your display name, featured services, and visibility preferences.",
      },
      {
        title: "Manage Leads",
        description:
          "View and respond to customer inquiries that come through the Store Locator. Each lead includes customer contact info, the product they're interested in, and their message.",
      },
      {
        title: "Upload Gallery Photos",
        description:
          "Add high-quality photos of your showroom, installations, and products. Gallery images appear on your locator listing and help convert browsing customers into leads.",
      },
      {
        title: "List Your Services",
        description:
          "Specify the services you offer (installation, consultation, delivery, etc.) so customers can filter dealers by service type on the locator.",
      },
    ],
    tips: [
      { type: "info", text: "This feature is currently marked as 'Coming Soon'. You'll receive a notification when it goes live." },
      { type: "tip", text: "Start preparing your gallery images and service list now so you're ready to launch when the locator goes live." },
    ],
    features: [
      "Public dealer locator map with search and filtering",
      "Lead capture and management system",
      "Photo gallery with lightbox viewer",
      "Service listing with category-based filtering",
      "Social media link integration",
    ],
  },
  {
    id: "page-claim",
    title: "Page Claim Guide",
    icon: FiShield,
    shortDescription: "Verify ownership of your dealer listing, unlock editing access, and manage removal requests.",
    path: "/claim-page",
    overview:
      "The Page Claim section allows verified dealers to take official ownership of their public dealer locator page. By claiming your page, you unlock full editing privileges to personalize your business showcase, manage customer leads directly, build buyer trust with verified badges, and monitor real-time visitor analytics. If an existing listing is incorrect or obsolete, you can also submit an official removal request.",
    steps: [
      {
        title: "1. Eligibility Requirements",
        description:
          "To maintain trust and data authenticity across the dealer network, page claiming requires a verified purchasing record of at least 4 completed wholesale orders. Your current order eligibility is displayed automatically on the claim status overview.",
      },
      {
        title: "2. Submitting a Claim Request",
        description:
          "If your account meets the 4-order eligibility threshold, click the 'Claim Page' button to submit your verification request. Our admin team reviews and validates your listing to ensure accurate company assignment.",
      },
      {
        title: "3. Pending Approval & Status Badges",
        description:
          "Once submitted, your page transitions to 'Claim Request Pending Approval'. You will see real-time status banners on your portal. You do not need to resubmit while review is underway.",
      },
      {
        title: "4. Editing Your Live Page",
        description:
          "Upon approval, your page status changes to 'Live'. The button switches to 'Edit Your Page', allowing you to customize your bio, showroom address, phone numbers, installation services, and social links at /page-edit/current.",
      },
      {
        title: "5. Requesting Listing Removal",
        description:
          "If a location has closed, relocated, or requires delisting from the public locator, click 'Request Removal'. Provide confirmation in the modal to flag the profile for administrative review and safe archiving.",
      },
    ],
    tips: [
      { type: "info", text: "Page claiming requires at least 4 wholesale orders on your account to activate." },
      { type: "tip", text: "Once your page is claimed and live, keeping your showroom photos and operating hours updated significantly boosts customer inquiry rates." },
      { type: "warning", text: "Submitting a removal request will queue your page for delisting from the public locator upon admin review." },
    ],
    features: [
      "Order-based eligibility verification (4+ orders required)",
      "Instant status tracking (Pending, Live, Removal Pending)",
      "Direct shortcut to the live page editor",
      "Listing removal workflow for closed or updated locations",
      "Performance metrics: page views, claim count, and customer requests",
    ],
  },
  {
    id: "dealer-program",
    title: "Dealer Program",
    icon: FiAward,
    shortDescription: "Understand your tier benefits, pricing, and program structure.",
    path: "/dealer-program",
    overview:
      "The Dealer Program page provides a comprehensive overview of the DECO TV Frames dealer tier system. Learn about the benefits at each level (Bronze, Silver, Gold, Platinum), understand pricing structures, and see exactly what you need to achieve to reach the next tier.",
    steps: [
      {
        title: "Review Tier Benefits",
        description:
          "Each tier unlocks progressively better benefits including deeper wholesale discounts, priority support, marketing materials, and exclusive product access.",
      },
      {
        title: "Understand Revenue Thresholds",
        description:
          "See the exact revenue targets for each tier. Your 365-day rolling revenue determines your current tier, and upgrades happen automatically when you cross a threshold.",
      },
      {
        title: "Explore Pricing Tables",
        description:
          "View the wholesale pricing matrix broken down by tier. Higher tiers enjoy lower per-unit costs, which directly improves your profit margins.",
      },
    ],
    tips: [
      { type: "tip", text: "Focus on your 365-day rolling revenue, not lifetime. Consistent ordering throughout the year is the key to maintaining a high tier." },
      { type: "info", text: "Tier downgrades are evaluated quarterly. If your rolling revenue drops below your current tier threshold, you may be reassigned at the next review." },
    ],
    features: [
      "Interactive tier comparison chart",
      "Detailed benefits breakdown per tier",
      "Wholesale pricing matrix with tier-based discounts",
      "Revenue threshold visualization",
    ],
  },
  {
    id: "profit-calculator",
    title: "Profit Calculator",
    icon: FiPercent,
    shortDescription: "Estimate your margins and profitability per frame.",
    path: "/profit-calculator",
    overview:
      "The Profit Calculator is a powerful tool that helps you estimate your profit margins on each DECO TV Frames product. Input your retail selling price and the calculator will show you your wholesale cost (based on your current tier), gross profit, and margin percentage.",
    steps: [
      {
        title: "Select a Product",
        description:
          "Choose from the catalog of available DECO TV Frames products. Each product has a different wholesale cost based on your current tier.",
      },
      {
        title: "Enter Your Retail Price",
        description:
          "Input the price you plan to charge your customers. The calculator will automatically compute your gross profit and margin percentage.",
      },
      {
        title: "Compare Across Tiers",
        description:
          "Toggle between tier levels to see how upgrading your tier would affect your profit margins. This can motivate you to push toward the next revenue threshold.",
      },
    ],
    tips: [
      { type: "tip", text: "Use the calculator before setting retail prices to ensure you're hitting your target margin. Most successful dealers aim for 40-60% margins." },
      { type: "info", text: "The wholesale costs shown are based on your current tier. As you tier up, your costs decrease and margins improve automatically." },
    ],
    features: [
      "Real-time profit margin calculation",
      "Tier-based wholesale cost display",
      "Cross-tier margin comparison",
      "Product catalog integration",
    ],
  },
  {
    id: "sales-resources",
    title: "Sales Resources",
    icon: FiBox,
    shortDescription: "Browse demo products, manage your showroom, and request demos.",
    path: "/sales-resources",
    overview:
      "Sales Resources is your one-stop shop for demo products, showroom inventory tracking, and sales tools. Browse the full catalog of demo frames, request demo units for your showroom, track what's currently on display, and order directly from Shopify — all within the portal.",
    steps: [
      {
        title: "View Your Showroom Inventory",
        description:
          "The 'On Display in My Showroom' section shows your current demo inventory. Items you've acquired appear with a green 'Acquired' badge, while required items you haven't purchased yet show 'Order Now' and 'Request Demo' buttons.",
      },
      {
        title: "Browse Demo Products",
        description:
          "Scroll through the full demo product catalog. Each card shows the product image, title, and available actions. Products are pulled live from the Shopify catalog.",
      },
      {
        title: "Request a Demo Frame",
        description:
          "Click 'Request Demo' on any product to send a demo request to your sales representative. You can include a custom message explaining your needs.",
      },
      {
        title: "Order Products Directly",
        description:
          "Click 'Order Now' to be taken directly to the product page on framemytv.com where you can complete your purchase with your dealer pricing.",
      },
    ],
    tips: [
      { type: "tip", text: "Having all three core showroom items (Mobile Kit, Premiere Corner Samples, Alloy Corner Samples) demonstrates commitment and can help you close more sales." },
      { type: "info", text: "Demo requests are sent to your assigned sales representative who will follow up with availability and shipping details." },
      { type: "warning", text: "Demo products are subject to availability. Popular items may have a wait time during peak seasons." },
    ],
    features: [
      "Live Shopify product catalog with images and details",
      "Showroom inventory tracker with acquisition status",
      "One-click demo request workflow",
      "Direct ordering to framemytv.com",
      "Smart deduplication — items won't show twice if already tracked",
    ],
  },
  {
    id: "digital-art",
    title: "Digital Art Collection",
    icon: FiImage,
    shortDescription: "Explore and download curated high-resolution digital artwork for Samsung Frame TVs.",
    path: "/digital-art",
    overview:
      "The Digital Art page provides dealers and customers with direct access to Deco TV Frames' official digital artwork catalog hosted on Canto. Designed to complement Samsung Frame TVs, this collection features categorized high-resolution art albums ranging from Impressionist and Abstract to Landscapes and Cityscapes.",
    steps: [
      {
        title: "1. Browse Featured Artwork Carousel",
        description:
          "The interactive wall preview carousel at the top automatically rotates through highlighted categories. Click any card in the carousel or grid to view its album.",
      },
      {
        title: "2. Explore Categories",
        description:
          "Browse through all 12 art categories. Each category displays a cover image, title, and direct link to view the complete Canto high-resolution album.",
      },
      {
        title: "3. Open Full Canto Collection",
        description:
          "Click 'Browse Full Collection' or 'Open Digital Art Gallery' to access the entire Canto asset repository where you can search, view, and download artwork for display.",
      },
    ],
    tips: [
      { type: "tip", text: "Digital artwork files are optimized for Samsung Frame TV resolution (3840 x 2160) for seamless display." },
      { type: "info", text: "New seasonal and themed artwork collections are added regularly to Canto." },
    ],
    features: [
      "Direct integration with Canto digital asset management",
      "Auto-rotating interactive wall gallery hero preview",
      "12 curated artwork categories (Landscapes, Abstract, Impressionist, etc.)",
      "High-resolution digital downloads optimized for 4K TV screens",
    ],
  },
];
